"use client";
import { useState, useEffect, useRef, useCallback } from "react";
import SetupPane from "../components/setup/setup";
import HzDashboard from "../components/HzDashboard";
import ToastContainer from "../components/ToastContainer";
import { useToast } from "../hooks/useToast";
import Header from "../components/Header";
import Practiceswiper from "../components/tutorial/Practiceswiper";
import {
  FaInstagram as FaInstagramRaw,
  FaWhatsapp as FaWhatsappRaw,
  FaYoutube as FaYoutubeRaw,
  FaFacebookF as FaFacebookRaw,
} from "react-icons/fa";
import SocialQrModal from "../components/tutorial/SocialQrModal";
import { playOnMultipleDevices, stopAll, pauseAll, resumeAll, setTrack } from "../services/music.service";

import { readBattery, onBatteryChange, disconnectEyeDevice } from "../services/eyeBluetooth.service";
import { getAudioOutputDevices } from "../services/audioDevices.service";
import "./home.css";
import SimpleView from "components/views/simpleView";
import { FONT_MONO } from "theme/fonts";

/* ================= TYPES ================= */

type Amplitude = {
  ch1: number;
  ch2: number;
  ch3: number;
};

type HzHistoryItem = {
  time: number;
  hz: number;
};

type EogHistoryItem = {
  time: number;
  ch1: number;
  ch2: number;
  ch3: number;
};

type HomeProps = {
  user: any;
};

type EyeStateType = "open" | "half" | "closed" | "dreaming";

/* ================= CONSTANTS ================= */

const TRACKS = [
  {
    id: "interstellar",
    label: "Interstellar",
    artist: "Hans Zimmer",
    file: "/music/interstellar.mp3",
    emoji: "🌌",
    default: true,
  },
];

const BATTERY_POLL_MS = 30000;
const MAX_HISTORY = 500;
const BOTH_CONNECTED_DEBOUNCE_MS = 1500;

const SIGNAL_LOSS_GRACE_MS = 10_000;

const REM_CHECK_INTERVAL_MS = 10_000;
const REM_WINDOW_MS = 10_000;
// REM detection threshold — fixed, NOT controlled by admin slider anymore
const DEFAULT_REM_THRESHOLD = 250;
const CONSECUTIVE_WINDOWS = 1;

const AFFIRMATION_AUDIO_SRC = "/audio/stay-aware.mp3";
const STAY_CONSCIOUS_AUDIO_SRC = "/audio/stay-conscious.mp3";

// ── Reactive affirmation volume config ────────────────────────────────────
// Volume reacts to live eye-movement signal. The admin slider now controls
// REACTIVE_AUDIO_NORM_DELTA — the avgDelta value that maps to "full volume".
// Lower value = more sensitive (quieter movement = max volume).
// Higher value = less sensitive (needs stronger movement to reach max volume).
const REACTIVE_AUDIO_MIN_GAIN = 0.015; // floor — always at least this quiet
const REACTIVE_AUDIO_MAX_GAIN = 0.05; // ceiling — never louder than this
const REACTIVE_AUDIO_WINDOW_MS = 10_000; // how far back to look for movement
const REACTIVE_AUDIO_UPDATE_MS = 2_000; // how often we re-evaluate volume
const REACTIVE_AUDIO_RAMP_SEC = 2.5; // smooth ramp — no snapping
const DEFAULT_AUDIO_SENSITIVITY = 250; // default normDelta (admin-adjustable)
const DEFAULT_AFFIRMATION_INTERVAL_SEC = 60; // default gap between affirmation clips

// ── Hypnagogic audio session config ──────────────────────────────────────
const HYPNAGOGIC_SESSION = {
  harmonics: [
    [73.4, 0.52],
    [110.1, 0.24],
    [146.8, 0.11],
    [220.2, 0.05],
  ] as [number, number][],
  lfoFreq: 0.042,
  lfoGain: 0,
  leftHz: 180,
  rightHz: 183.5,
  binGain: 0,
  delayTime: 0.65,
  delayFb: 0.24,
  masterGain: 0.036,
  fadeIn: 4.0,
  binFadeIn: 6.0,
};

// ── Admin detection ───────────────────────────────────────────────────────
function isAdminUser(user: any): boolean {
  if (!user) return false;
  if (user.role === "admin") return true;
  if (user.isAdmin === true) return true;
  if (typeof user.email === "string" && (user.email.endsWith("trip123245@gmail.com") || user.email.endsWith("monishganesan2000@gmail.com")))
    return true;
  return false;
}

/* ================= PRACTICE SWIPER WRAPPER ================= */
function PracticeSwiperWrapper({
  onSkip,
  onComplete,
  isAudioPlaying,
}: {
  onSkip: () => void;
  onComplete: () => void;
  isAudioPlaying?: boolean;
}) {
  useEffect(() => {
    const t = setTimeout(() => {
      window.dispatchEvent(new Event("yogyism:startPractice"));
    }, 80);
    return () => clearTimeout(t);
  }, []);

  return <Practiceswiper onSkip={onSkip} onComplete={onComplete} isAudioPlaying={isAudioPlaying} />;
}

/* ================= HOOK: detect mobile ================= */
function useIsMobile(breakpoint = 900) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= breakpoint);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    setIsMobile(mq.matches);
    return () => mq.removeEventListener("change", handler);
  }, [breakpoint]);
  return isMobile;
}

/* ================= HOOK: stable bothConnected ================= */
function useDebouncedBothConnected(raw: boolean, debounceMs: number): boolean {
  const [stable, setStable] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (raw) {
      timerRef.current = setTimeout(() => setStable(true), debounceMs);
    } else {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setStable(false);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [raw, debounceMs]);
  return stable;
}

/* ================= HOOK: useSignalLossGrace ================= */
interface SignalLossGraceResult {
  gracefulValue: boolean;
  inGracePeriod: boolean;
  gracePctLeft: number;
  graceSecondsElapsed: number;
}

function useSignalLossGrace(raw: boolean, graceMs: number): SignalLossGraceResult {
  const [gracefulValue, setGracefulValue] = useState(raw);
  const [inGracePeriod, setInGracePeriod] = useState(false);
  const [gracePctLeft, setGracePctLeft] = useState(1);
  const [graceSecondsElapsed, setGraceSecondsElapsed] = useState(0);

  const graceStartRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const expireTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startAnimation = useCallback(() => {
    const tick = () => {
      if (graceStartRef.current === null) return;
      const elapsed = Date.now() - graceStartRef.current;
      const pct = Math.max(0, 1 - elapsed / graceMs);
      const secs = Math.floor(elapsed / 1000);
      setGracePctLeft(pct);
      setGraceSecondsElapsed(secs);
      if (pct > 0) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [graceMs]);

  const stopAnimation = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const clearExpireTimer = useCallback(() => {
    if (expireTimerRef.current !== null) {
      clearTimeout(expireTimerRef.current);
      expireTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (raw) {
      clearExpireTimer();
      stopAnimation();
      graceStartRef.current = null;
      setGracefulValue(true);
      setInGracePeriod(false);
      setGracePctLeft(1);
      setGraceSecondsElapsed(0);
    } else {
      if (graceStartRef.current !== null) return;

      setGracefulValue((prev) => {
        if (!prev) return false;
        graceStartRef.current = Date.now();
        setInGracePeriod(true);
        setGracePctLeft(1);
        setGraceSecondsElapsed(0);
        startAnimation();

        expireTimerRef.current = setTimeout(() => {
          graceStartRef.current = null;
          setGracefulValue(false);
          setInGracePeriod(false);
          setGracePctLeft(0);
          setGraceSecondsElapsed(0);
          stopAnimation();
        }, graceMs);

        return true;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw]);

  useEffect(() => {
    return () => {
      clearExpireTimer();
      stopAnimation();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { gracefulValue, inGracePeriod, gracePctLeft, graceSecondsElapsed };
}

/* ================= HOOK: REM / dreaming detector ================= */
// NOTE: REM detection is kept fully intact and independent.
// It still transitions eyeState → "dreaming" when avgDelta >= remThreshold.
// It is NOT connected to audio triggering anymore — audio starts at practice
// complete and volume is driven purely by audioSensitivity (admin slider).
function useRemDetector({
  eogHistory,
  active,
  currentEyeState,
  onStateChange,
  remThreshold,
  debugLogging,
}: {
  eogHistory: EogHistoryItem[];
  active: boolean;
  currentEyeState: EyeStateType;
  onStateChange: (state: "half" | "closed" | "dreaming") => void;
  remThreshold: number;
  debugLogging: boolean;
}) {
  const historyRef = useRef<EogHistoryItem[]>(eogHistory);
  const activeRef = useRef<boolean>(active);
  const eyeStateRef = useRef<string>(currentEyeState);
  const remConsecutive = useRef<number>(0);
  const calmConsecutive = useRef<number>(0);
  const thresholdRef = useRef<number>(remThreshold);
  const debugRef = useRef<boolean>(debugLogging);

  useEffect(() => {
    historyRef.current = eogHistory;
  }, [eogHistory]);
  useEffect(() => {
    activeRef.current = active;
  }, [active]);
  useEffect(() => {
    eyeStateRef.current = currentEyeState;
  }, [currentEyeState]);
  useEffect(() => {
    thresholdRef.current = remThreshold;
  }, [remThreshold]);
  useEffect(() => {
    debugRef.current = debugLogging;
  }, [debugLogging]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!activeRef.current) return;

      const now = Date.now();
      const cutoff = now - REM_WINDOW_MS;
      const window = historyRef.current.filter((e) => e.time >= cutoff);

      if (window.length < 5) return;

      let totalDelta = 0,
        count = 0;
      for (let i = 1; i < window.length; i++) {
        totalDelta += Math.abs(window[i].ch1 - window[i - 1].ch1);
        count++;
      }
      const avgDelta = count > 0 ? totalDelta / count : 0;
      const isREM = avgDelta >= thresholdRef.current;

      if (isREM) {
        remConsecutive.current++;
        calmConsecutive.current = 0;
        if (remConsecutive.current >= CONSECUTIVE_WINDOWS && eyeStateRef.current !== "dreaming") {
          onStateChange("dreaming");
        }
      } else {
        calmConsecutive.current++;
        remConsecutive.current = 0;
        if (calmConsecutive.current >= CONSECUTIVE_WINDOWS && eyeStateRef.current === "dreaming") {
          onStateChange("half");
        }
      }
    }, REM_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/* ================= REACTIVE DREAM AUDIO — CONTINUOUS, VOLUME-REACTIVE =====
 * Both clips loop for the entire session starting right after practice
 * ends/is skipped, but they are NOT chained back-to-back anymore.
 *
 * CHANGE (interval fix): Each clip's "ended" event now schedules the next
 * clip after `intervalSec` seconds of silence instead of playing it
 * immediately — this is what makes the Affirmation Timer slider in Step 4
 * actually control the spacing between affirmations. Previously this value
 * was captured by the UI but never reached the audio engine at all, so
 * changing the slider had zero effect on playback.
 * If intervalSec === 0 ("Affirmations Off" in the UI), no clip is scheduled
 * automatically — only a manual "Play Now" from the admin panel will play one.
 *
 * Volume is still driven by `audioSensitivity` (the admin slider value):
 * lower = reaches max volume with less eye movement, higher = needs more.
 * ============================================================================ */
type ReactiveStep = "you-are-dreaming" | "stay-conscious" | "stopped";

interface ReactiveDreamAudioManager {
  start: () => void;
  unlock: () => void;
  playNow: () => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  setIntensity: (intensity01: number) => void;
  setIntensityWithRange: (intensity01: number, minGain: number, maxGain: number) => void;
  setIntervalSeconds: (secs: number) => void;
  setStepChangeHandler: (fn: ((label: string | null) => void) | null) => void;
  currentStep: () => ReactiveStep;
}

function labelForReactiveStep(step: ReactiveStep): string | null {
  if (step === "you-are-dreaming") return "Stay aware…";
  if (step === "stay-conscious") return "Stay conscious…";
  return null;
}

function createReactiveDreamAudioManager(): ReactiveDreamAudioManager {
  let ctx: AudioContext | null = null;
  let gainNode: GainNode | null = null;
  let dreamingEl: HTMLAudioElement | null = null;
  let consciousEl: HTMLAudioElement | null = null;
  let dreamingSrcNode: MediaElementAudioSourceNode | null = null;
  let consciousSrcNode: MediaElementAudioSourceNode | null = null;

  let running = false;
  let paused = false;
  let step: ReactiveStep = "stopped";
  let lastAppliedGain = REACTIVE_AUDIO_MIN_GAIN;

  // ── Interval-gap scheduling state ──
  let intervalSec: number = DEFAULT_AFFIRMATION_INTERVAL_SEC;
  let pendingTimeout: ReturnType<typeof setTimeout> | null = null;
  let awaitingStep: ReactiveStep | null = null;
  let stepChangeCb: ((label: string | null) => void) | null = null;

  const clearPending = () => {
    if (pendingTimeout) {
      clearTimeout(pendingTimeout);
      pendingTimeout = null;
    }
    awaitingStep = null;
  };

  const ensureGraph = () => {
    if (ctx) return;
    const W = window as typeof window & {
      webkitAudioContext?: typeof AudioContext;
    };
    const Ctx = window.AudioContext ?? W.webkitAudioContext;
    if (!Ctx) {
      console.warn("Web Audio API not supported — reactive affirmation audio disabled");
      return;
    }

    ctx = new Ctx();
    gainNode = ctx.createGain();
    gainNode.gain.setValueAtTime(REACTIVE_AUDIO_MIN_GAIN, ctx.currentTime);
    gainNode.connect(ctx.destination);

    dreamingEl = new Audio(AFFIRMATION_AUDIO_SRC);
    consciousEl = new Audio(STAY_CONSCIOUS_AUDIO_SRC);
    dreamingEl.loop = false;
    consciousEl.loop = false;
    dreamingEl.crossOrigin = "anonymous";
    consciousEl.crossOrigin = "anonymous";

    dreamingSrcNode = ctx.createMediaElementSource(dreamingEl);
    consciousSrcNode = ctx.createMediaElementSource(consciousEl);
    dreamingSrcNode.connect(gainNode);
    consciousSrcNode.connect(gainNode);

    // On "ended", schedule the *next* clip after intervalSec seconds of
    // silence rather than chaining straight into it.
    dreamingEl.addEventListener("ended", () => {
      if (!running || paused) return;
      playFrom("stay-conscious"); // was: scheduleNext("stay-conscious")
    });
    consciousEl.addEventListener("ended", () => {
      if (!running || paused) return;
      scheduleNext("you-are-dreaming"); // unchanged — this is the real gap
    });
  };

  const playFrom = (which: ReactiveStep) => {
    if (!dreamingEl || !consciousEl) return;
    dreamingEl.pause();
    dreamingEl.currentTime = 0;
    consciousEl.pause();
    consciousEl.currentTime = 0;
    step = which;
    awaitingStep = null;
    if (which === "you-are-dreaming") {
      dreamingEl.play().catch((e) => console.warn("[ReactiveDreamAudio] start play failed:", e));
    } else if (which === "stay-conscious") {
      consciousEl.play().catch((e) => console.warn("[ReactiveDreamAudio] start play failed:", e));
    }
    stepChangeCb?.(labelForReactiveStep(step));
  };

  const unlock = () => {
    ensureGraph();
    if (!dreamingEl || !consciousEl) return;
    [dreamingEl, consciousEl].forEach((audio) => {
      // iOS accepts a zero-volume play from the gesture, but muted priming
      // can leave later timer-driven playback blocked.
      const previousVolume = audio.volume;
      audio.volume = 0;
      audio.currentTime = 0;
      audio
        .play()
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          audio.volume = previousVolume;
        })
        .catch(() => {
          audio.volume = previousVolume;
        });
    });
  };

  // Waits `intervalSec` seconds of silence, then plays `which`. If the
  // interval is 0 ("Affirmations Off"), nothing is scheduled — playback
  // only resumes via a manual playNow() call.
  const scheduleNext = (which: ReactiveStep) => {
    clearPending();
    stepChangeCb?.(null); // clear the "now playing" label during the gap
    if (intervalSec <= 0) return;
    awaitingStep = which;
    pendingTimeout = setTimeout(() => {
      pendingTimeout = null;
      const target = awaitingStep;
      awaitingStep = null;
      if (!running || paused || !target) return;
      playFrom(target);
    }, intervalSec * 1000);
  };

  return {
    unlock,
    start() {
      ensureGraph();
      if (!ctx || !gainNode) return;
      ctx.resume().catch(() => {});
      running = true;
      paused = false;
      lastAppliedGain = REACTIVE_AUDIO_MIN_GAIN;
      gainNode.gain.cancelScheduledValues(ctx.currentTime);
      gainNode.gain.setValueAtTime(REACTIVE_AUDIO_MIN_GAIN, ctx.currentTime);
      clearPending();
      if (intervalSec > 0) {
        playFrom("you-are-dreaming");
      } else {
        // Affirmations off — stay silent until a manual "Play Now"
        step = "stopped";
        stepChangeCb?.(null);
      }
    },

    playNow() {
      ensureGraph();
      if (!ctx || !gainNode) return;
      ctx.resume().catch(() => {});
      running = true;
      paused = false;
      clearPending();
      playFrom(step === "stay-conscious" ? "stay-conscious" : "you-are-dreaming");
    },

    pause() {
      if (!running || !dreamingEl || !consciousEl) return;
      paused = true;
      dreamingEl.pause();
      consciousEl.pause();
      if (pendingTimeout) {
        clearTimeout(pendingTimeout);
        pendingTimeout = null;
      }
      stepChangeCb?.(null);
    },

    resume() {
      if (!running || !dreamingEl || !consciousEl) return;
      paused = false;
      if (awaitingStep) {
        // We were mid-gap when paused — restart the wait for that clip.
        scheduleNext(awaitingStep);
        return;
      }
      if (step === "you-are-dreaming") {
        dreamingEl.play().catch(() => {});
        stepChangeCb?.(labelForReactiveStep(step));
      } else if (step === "stay-conscious") {
        consciousEl.play().catch(() => {});
        stepChangeCb?.(labelForReactiveStep(step));
      }
    },

    stop() {
      running = false;
      paused = false;
      step = "stopped";
      clearPending();
      if (dreamingEl) {
        dreamingEl.pause();
        dreamingEl.currentTime = 0;
      }
      if (consciousEl) {
        consciousEl.pause();
        consciousEl.currentTime = 0;
      }
      if (gainNode && ctx) {
        gainNode.gain.cancelScheduledValues(ctx.currentTime);
        gainNode.gain.setValueAtTime(REACTIVE_AUDIO_MIN_GAIN, ctx.currentTime);
      }
      lastAppliedGain = REACTIVE_AUDIO_MIN_GAIN;
      stepChangeCb?.(null);
    },

    setIntensityWithRange(intensity01: number, minGain: number, maxGain: number) {
      if (!ctx || !gainNode || !running || paused) return;
      const clamped = Math.max(0, Math.min(1, intensity01));
      const target = minGain + (maxGain - minGain) * clamped;
      const now = ctx.currentTime;
      gainNode.gain.cancelScheduledValues(now);
      gainNode.gain.setValueAtTime(lastAppliedGain, now);
      gainNode.gain.linearRampToValueAtTime(target, now + REACTIVE_AUDIO_RAMP_SEC);
      lastAppliedGain = target;
    },

    setIntensity(intensity01: number) {
      if (!ctx || !gainNode || !running || paused) return;
      const clamped = Math.max(0, Math.min(1, intensity01));
      const target = REACTIVE_AUDIO_MIN_GAIN + (REACTIVE_AUDIO_MAX_GAIN - REACTIVE_AUDIO_MIN_GAIN) * clamped;
      const now = ctx.currentTime;
      gainNode.gain.cancelScheduledValues(now);
      gainNode.gain.setValueAtTime(lastAppliedGain, now);
      gainNode.gain.linearRampToValueAtTime(target, now + REACTIVE_AUDIO_RAMP_SEC);
      lastAppliedGain = target;
    },

    setIntervalSeconds(secs: number) {
      intervalSec = Math.max(0, Math.floor(secs));
      // If affirmations were just turned OFF mid-gap, cancel the pending clip.
      if (intervalSec <= 0) clearPending();
    },

    setStepChangeHandler(fn) {
      stepChangeCb = fn;
    },

    currentStep() {
      return step;
    },
  };
}

/* ================= HOOK: useReactiveDreamAudio =================
 * CHANGE: Now accepts `affirmationIntervalSecs` (wired to the Step 4
 * "Affirmation Timer" slider) and `onLabelChange` (fires with a
 * human-readable label — "You are dreaming…" / "Stay conscious…" / null —
 * whenever the currently-playing clip changes, so the UI can show it live).
 * ================================================================= */
function useReactiveDreamAudio({
  active,
  eogHistory,
  manualPaused,
  audioSensitivity,
  audioMinGain,
  audioMaxGain,
  affirmationIntervalSecs,
  onLabelChange,
}: {
  active: boolean;
  eogHistory: EogHistoryItem[];
  manualPaused: boolean;
  audioSensitivity: number;
  audioMinGain: number;
  audioMaxGain: number;
  affirmationIntervalSecs: number;
  onLabelChange?: (label: string | null) => void;
}) {
  const minGainRef = useRef<number>(audioMinGain);
  const maxGainRef = useRef<number>(audioMaxGain);
  useEffect(() => {
    minGainRef.current = audioMinGain;
  }, [audioMinGain]);
  useEffect(() => {
    maxGainRef.current = audioMaxGain;
  }, [audioMaxGain]);
  const managerRef = useRef<ReactiveDreamAudioManager | null>(null);
  const runningRef = useRef<boolean>(false);
  const historyRef = useRef<EogHistoryItem[]>(eogHistory);
  const sensitivityRef = useRef<number>(audioSensitivity);
  const onLabelChangeRef = useRef<typeof onLabelChange>(onLabelChange);

  if (!managerRef.current) {
    managerRef.current = createReactiveDreamAudioManager();
  }

  useEffect(() => {
    historyRef.current = eogHistory;
  }, [eogHistory]);
  useEffect(() => {
    sensitivityRef.current = audioSensitivity;
  }, [audioSensitivity]);
  useEffect(() => {
    onLabelChangeRef.current = onLabelChange;
  }, [onLabelChange]);

  // Register the step-change handler once. It always calls through the
  // latest onLabelChange via the ref, so it never goes stale even though
  // it's only registered a single time.
  useEffect(() => {
    managerRef.current!.setStepChangeHandler((label) => onLabelChangeRef.current?.(label));
    return () => {
      managerRef.current?.setStepChangeHandler(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the manager's interval-gap in sync with the Affirmation Timer slider —
  // this is the actual fix: previously nothing ever called this.
  useEffect(() => {
    managerRef.current!.setIntervalSeconds(affirmationIntervalSecs);
  }, [affirmationIntervalSecs]);

  // Start / stop the continuous loop based on `active`
  useEffect(() => {
    const mgr = managerRef.current!;
    if (active) {
      if (!runningRef.current) {
        runningRef.current = true;
        mgr.start();
      }
    } else {
      if (runningRef.current) {
        runningRef.current = false;
        mgr.stop();
      }
    }
  }, [active]);

  // Manual pause/resume override from admin panel
  useEffect(() => {
    const mgr = managerRef.current!;
    if (manualPaused) {
      mgr.pause();
    } else if (active && runningRef.current) {
      mgr.resume();
    }
  }, [manualPaused, active]);

  // Periodically compute intensity using live audioSensitivity from admin slider
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => {
      const now = Date.now();
      const cutoff = now - REACTIVE_AUDIO_WINDOW_MS;
      const window = historyRef.current.filter((e) => e.time >= cutoff);
      if (window.length < 5) return;

      let totalDelta = 0,
        count = 0;
      for (let i = 1; i < window.length; i++) {
        totalDelta += Math.abs(window[i].ch1 - window[i - 1].ch1);
        count++;
      }
      const avgDelta = count > 0 ? totalDelta / count : 0;
      // Use live sensitivityRef so admin slider changes take effect immediately
      const normDelta = Math.max(1, sensitivityRef.current);
      const intensity = Math.max(0, Math.min(1, avgDelta / normDelta));
      // Pass live min/max so user slider changes take effect immediately
      managerRef.current!.setIntensityWithRange(intensity, minGainRef.current, maxGainRef.current);
    }, REACTIVE_AUDIO_UPDATE_MS);
    return () => clearInterval(id);
  }, [active]);

  const playNow = useCallback(() => {
    managerRef.current!.playNow();
  }, []);

  const stopEverything = useCallback(() => {
    runningRef.current = false;
    managerRef.current!.stop();
  }, []);

  const unlockAudio = useCallback(() => {
    managerRef.current!.unlock();
  }, []);

  useEffect(() => {
    const handleGestureUnlock = () => {
      unlockAudio();
    };

    window.addEventListener("pointerdown", handleGestureUnlock, { passive: true });
    window.addEventListener("touchstart", handleGestureUnlock, { passive: true });
    window.addEventListener("keydown", handleGestureUnlock, { passive: true });

    return () => {
      window.removeEventListener("pointerdown", handleGestureUnlock);
      window.removeEventListener("touchstart", handleGestureUnlock);
      window.removeEventListener("keydown", handleGestureUnlock);
    };
  }, [unlockAudio]);

  return { restartCycleNow: playNow, stopEverything, unlockAudio };
}

/* ================= HYPNAGOGIC AUDIO HOOK ================================= */
interface HypnagogicAudio {
  startHypnagogic: (volume?: number) => void;
  stopHypnagogic: (fadeSecs?: number) => void;
  isHypnagogicOn: boolean;
}

function useHypnagogicAudio(): HypnagogicAudio {
  const ctxRef = useRef<AudioContext | null>(null);
  const masterRef = useRef<GainNode | null>(null);
  const binRef = useRef<GainNode | null>(null);
  const oscsRef = useRef<OscillatorNode[]>([]);
  const binOscsRef = useRef<OscillatorNode[]>([]);
  const lfoRef = useRef<OscillatorNode | null>(null);
  const [isOn, setIsOn] = useState(false);

  const stopHypnagogic = useCallback((fadeSecs = 2.5) => {
    if (!ctxRef.current) return;
    const ctx = ctxRef.current;
    const now = ctx.currentTime;

    masterRef.current?.gain.cancelScheduledValues(now);
    masterRef.current?.gain.linearRampToValueAtTime(0, now + fadeSecs);
    binRef.current?.gain.cancelScheduledValues(now);
    binRef.current?.gain.linearRampToValueAtTime(0, now + fadeSecs);

    setTimeout(
      () => {
        [...oscsRef.current, ...binOscsRef.current].forEach((o) => {
          try {
            o.stop();
          } catch (_) {}
        });
        try {
          lfoRef.current?.stop();
        } catch (_) {}
        ctx.close().catch(() => {});

        ctxRef.current = null;
        masterRef.current = null;
        binRef.current = null;
        lfoRef.current = null;
        oscsRef.current = [];
        binOscsRef.current = [];
      },
      (fadeSecs + 0.3) * 1000,
    );

    setIsOn(false);
  }, []);

  const startHypnagogic = useCallback((volume = 65) => {
    if (ctxRef.current) return;

    const W = window as typeof window & {
      webkitAudioContext?: typeof AudioContext;
    };
    const Ctx = window.AudioContext ?? W.webkitAudioContext;
    if (!Ctx) {
      console.warn("Web Audio API not supported");
      return;
    }

    const ctx = new Ctx();
    ctxRef.current = ctx;
    const now = ctx.currentTime;
    const S = HYPNAGOGIC_SESSION;
    const gainVal = (volume / 100) * S.masterGain;

    const master = ctx.createGain();
    master.gain.setValueAtTime(0, now);
    master.gain.linearRampToValueAtTime(gainVal, now + S.fadeIn);
    master.connect(ctx.destination);
    masterRef.current = master;

    const delay = ctx.createDelay(1.0);
    const fbG = ctx.createGain();
    delay.delayTime.value = S.delayTime;
    fbG.gain.value = S.delayFb;
    delay.connect(fbG);
    fbG.connect(delay);
    delay.connect(master);

    oscsRef.current = S.harmonics.map(([freq, amp]) => {
      const osc = ctx.createOscillator();
      const og = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      og.gain.value = amp;
      osc.connect(og);
      og.connect(delay);
      og.connect(master);
      osc.start();
      return osc;
    });

    const lfoNode = ctx.createOscillator();
    const lfoG = ctx.createGain();
    lfoNode.frequency.value = S.lfoFreq;
    lfoG.gain.value = S.lfoGain;
    lfoNode.connect(lfoG);
    lfoG.connect(master.gain);
    lfoNode.start();
    lfoRef.current = lfoNode;

    const merger = ctx.createChannelMerger(2);
    const bGain = ctx.createGain();
    bGain.gain.setValueAtTime(0, now);
    bGain.gain.linearRampToValueAtTime(S.binGain, now + S.binFadeIn);
    merger.connect(bGain);
    bGain.connect(ctx.destination);
    binRef.current = bGain;

    const lOsc = ctx.createOscillator(),
      lG = ctx.createGain();
    lOsc.type = "sine";
    lOsc.frequency.value = S.leftHz;
    lG.gain.value = 1;
    lOsc.connect(lG);
    lG.connect(merger, 0, 0);
    lOsc.start();

    const rOsc = ctx.createOscillator(),
      rG = ctx.createGain();
    rOsc.type = "sine";
    rOsc.frequency.value = S.rightHz;
    rG.gain.value = 1;
    rOsc.connect(rG);
    rG.connect(merger, 0, 1);
    rOsc.start();

    binOscsRef.current = [lOsc, rOsc];

    setIsOn(true);
    ctx.resume().then(() => {});
  }, []);

  useEffect(() => {
    return () => {
      stopHypnagogic(0.2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { startHypnagogic, stopHypnagogic, isHypnagogicOn: isOn };
}

/* ================= SIGNAL LOSS BANNER ================= */
interface SignalLossBannerProps {
  graceSecondsElapsed: number;
  gracePctLeft: number;
  graceTotalSec: number;
}

function SignalLossBanner({ graceSecondsElapsed, gracePctLeft, graceTotalSec }: SignalLossBannerProps) {
  const remaining = Math.max(0, graceTotalSec - graceSecondsElapsed);
  const barColor = gracePctLeft > 0.5 ? "#facc15" : gracePctLeft > 0.2 ? "#fb923c" : "#ef4444";

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        background: "rgba(10, 15, 28, 0.92)",
        backdropFilter: "blur(8px)",
        borderBottom: `1px solid ${barColor}55`,
        padding: "8px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 6,
        animation: "signalLossFadeIn 0.3s ease",
      }}
    >
      <style>{`
        @keyframes signalLossFadeIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 6,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: barColor,
              display: "inline-block",
              boxShadow: `0 0 8px ${barColor}`,
              animation: "signalDotPulse 1s ease-in-out infinite",
            }}
          />
          <style>{`
            @keyframes signalDotPulse {
              0%,100% { opacity: 1; }
              50%      { opacity: 0.3; }
            }
          `}</style>
          <span
            style={{
              fontFamily: FONT_MONO,
              fontSize: 11,
              fontWeight: 700,
              color: barColor,
              letterSpacing: 1.5,
              textTransform: "uppercase",
            }}
          >
            Signal lost — adjust sensor
          </span>
        </div>

        <div
          style={{
            fontFamily: FONT_MONO,
            fontSize: 12,
            fontWeight: 800,
            color: barColor,
            background: `${barColor}18`,
            border: `1px solid ${barColor}44`,
            borderRadius: 6,
            padding: "2px 10px",
            minWidth: 54,
            textAlign: "center",
            letterSpacing: 1,
          }}
        >
          {remaining}s
        </div>
      </div>

      <span
        style={{
          fontFamily: FONT_MONO,
          fontSize: 9,
          color: "#7aa8cc",
          letterSpacing: 0.5,
        }}
      >
        Resetting to setup screen in {remaining}s if not restored · current view held
      </span>

      <div
        style={{
          height: 3,
          background: "rgba(255,255,255,0.07)",
          borderRadius: 4,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${Math.round(gracePctLeft * 100)}%`,
            background: barColor,
            borderRadius: 4,
            transition: "width 0.25s linear, background 0.5s ease",
          }}
        />
      </div>
    </div>
  );
}
// react-icons v5's IconType returns ReactNode, which React 19's stricter JSX
// component typing rejects — cast once here rather than at every call site.
const FaInstagram = FaInstagramRaw as unknown as React.FC<{ size?: number }>;
const FaWhatsapp = FaWhatsappRaw as unknown as React.FC<{ size?: number }>;
const FaYoutube = FaYoutubeRaw as unknown as React.FC<{ size?: number }>;
const FaFacebook = FaFacebookRaw as unknown as React.FC<{ size?: number }>;

const SOCIAL_LINKS = {
  instagram: "https://instagram.com/yogyism",
  whatsapp: "https://chat.whatsapp.com/EIASdNv9ujzFKnGFmPfxBp",
  youtube: "https://www.youtube.com/@Yogyism",
  facebook: "https://www.facebook.com/profile.php?id=61591601232638",
};

const HOME_ACCENT = "#378add";

function hexToRgb(hex: string): string {
  const h = hex.replace("#", "");
  const full =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h;
  const n = parseInt(full, 16);
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
}
/* ================= COMPONENT ================= */

export default function Home({ user }: HomeProps) {
  const [hz, setHz] = useState<number>(0);
  const [connected, setConnected] = useState<boolean>(false);
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [showInstagram, setShowInstagram] = useState(false);
  const [showYoutube, setShowYoutube] = useState(false);
  const [showFacebook, setShowFacebook] = useState(false);
  const intentionalDisconnectRef = useRef(false);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [hzHistory, setHzHistory] = useState<HzHistoryItem[]>([]);
  const [showPractice, setShowPractice] = useState(false);
  const [showHzDashboard, setShowHzDashboard] = useState(false);
  const [eogHistory, setEogHistory] = useState<EogHistoryItem[]>([]);
  const [practiceSkipLoading, setPracticeSkipLoading] = useState(false);
  const [loaderMessage, setLoaderMessage] = useState<string>("Entering session…");
  const [currentAmplitude, setCurrentAmplitude] = useState<Amplitude>({
    ch1: 0,
    ch2: 0,
    ch3: 0,
  });
  const DEV_SKIP_PRACTICE = false;
  const DEV_MODE = false;
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [batteryLevel, setBatteryLevel] = useState<number | null>(null);
  const [showDashboard, setShowDashboard] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [sessionActive, setSessionActive] = useState(false);
  const [sessionReportOpen, setSessionReportOpen] = useState(false);
  const [advancedView, setAdvancedView] = useState(false);
  const [practiceCompleted, setPracticeCompleted] = useState(false);

  const isAdmin = isAdminUser(user);
  const [autoAffirmation, setAutoAffirmation] = useState<boolean>(true);

  // REM detection threshold — fixed at 500, NOT touched by admin slider anymore

  // ── NEW: audioSensitivity — controlled by admin slider ──────────────────
  // This replaces the old remThreshold slider. It sets how much eye movement
  // is needed to reach max affirmation volume.
  // Low value (e.g. 100) = very sensitive, max volume with small movements.
  // High value (e.g. 1000) = needs large movements to approach max volume.
  const [audioSensitivity, setAudioSensitivity] = useState<number>(DEFAULT_AUDIO_SENSITIVITY);
  const remThreshold = audioSensitivity;
  // ── NEW: user-configurable volume floor and ceiling ──
  const [audioMinGain, setAudioMinGain] = useState<number>(REACTIVE_AUDIO_MIN_GAIN);
  const [audioMaxGain, setAudioMaxGain] = useState<number>(REACTIVE_AUDIO_MAX_GAIN);
  const [adminEyeOverride, setAdminEyeOverride] = useState<EyeStateType | null>(null);
  const [dreamAudioPaused, setDreamAudioPaused] = useState(false);
  const [remDebugEnabled, setRemDebugEnabled] = useState(true);

  // ── Affirmation Timer (Step 4) — the actual gap, in seconds, between one
  // affirmation clip ending and the next one starting. This is the piece
  // that was previously missing: the slider updated local state in
  // SetupPane only, and never reached the audio engine.
  const [affirmationInterval, setAffirmationInterval] = useState<number>(DEFAULT_AFFIRMATION_INTERVAL_SEC);

  // ── Live "now playing" label for the currently active affirmation clip —
  // drives the small pill shown next to the Hypnagogic badge in the right panel.
  const [affirmationAudioLabel, setAffirmationAudioLabel] = useState<string | null>(null);

  // ── DEV_MODE auto-connect ────────────────────────────────────────────────
  // Previously this hardcoded a fake id "mock-device-1" that doesn't match
  // any real deviceId from getAudioOutputDevices() — so playback had no real
  // hardware to route to (this is the "mock speaker/mic" symptom). Now it
  // enumerates actual system audio outputs and selects a real one, falling
  // back to the browser's "default" output if enumeration fails or is empty.
  useEffect(() => {
    if (!DEV_MODE) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setConnected(true);
      setCurrentAmplitude({ ch1: 20, ch2: 10, ch3: 8 });
      try {
        const devices = await getAudioOutputDevices();
        if (cancelled) return;
        if (devices.length > 0) {
          setSelectedDeviceIds([devices[0].deviceId]);
        } else {
          setSelectedDeviceIds(["default"]);
        }
      } catch (err) {
        if (cancelled) return;
        console.warn("[DEV_MODE] Could not enumerate real audio output devices — falling back to system default.", err);
        setSelectedDeviceIds(["default"]);
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, []);

  const { toasts, toast, dismiss } = useToast();
  const isMobile = useIsMobile();

  const { startHypnagogic, stopHypnagogic, isHypnagogicOn } = useHypnagogicAudio();

  const isPlayingRef = useRef(false);
  const isPausedRef = useRef(false);
  const sessionActiveRef = useRef(false);
  const showPracticeRef = useRef(false);
  const amplitudeThrottleRef = useRef<number>(0);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);
  useEffect(() => {
    sessionActiveRef.current = sessionActive;
  }, [sessionActive]);
  useEffect(() => {
    showPracticeRef.current = showPractice;
  }, [showPractice]);
  useEffect(() => {
    setTrack(TRACKS[0].file);
  }, []);
  useEffect(() => {
    if (!isMobile) setPanelOpen(false);
  }, [isMobile]);

  /* ── Battery ── */
  useEffect(() => {
    if (!connected) {
      setBatteryLevel(null);
      onBatteryChange(null);
      return;
    }
    onBatteryChange((level: number) => setBatteryLevel(level));
    const poll = async () => {
      try {
        const l = await readBattery();
        if (l !== null) setBatteryLevel(l);
      } catch {}
    };
    const timer = setInterval(poll, BATTERY_POLL_MS);
    return () => clearInterval(timer);
  }, [connected]);

  /* ── Signal validation ── */
  const SIGNAL_THRESHOLD = 5;
  const REF_THRESHOLD = 2;
  const refConnected = connected && Math.abs(currentAmplitude.ch3) > REF_THRESHOLD;
  const signalValid = connected && refConnected && Math.abs(currentAmplitude.ch1) > SIGNAL_THRESHOLD;
  const rawBothConnected = connected && signalValid && selectedDeviceIds.length > 0;

  const bothConnectedDebounced = useDebouncedBothConnected(rawBothConnected, BOTH_CONNECTED_DEBOUNCE_MS);

  const {
    gracefulValue: bothConnected,
    inGracePeriod,
    gracePctLeft,
    graceSecondsElapsed,
  } = useSignalLossGrace(bothConnectedDebounced, SIGNAL_LOSS_GRACE_MS);

  /* ── Notify user when grace period begins / ends ── */
  const wasInGrace = useRef(false);
  useEffect(() => {
    if (inGracePeriod && !wasInGrace.current) {
      wasInGrace.current = true;
      if (sessionActive && !intentionalDisconnectRef.current) {
        toast({
          type: "warning",
          title: "⚠ Signal Lost",
          message: "Sensor signal interrupted. Adjust placement — 10s before reset.",
          duration: 5000,
        });
      }
    }
    if (!inGracePeriod && wasInGrace.current) {
      wasInGrace.current = false;
      if (bothConnected) {
        toast({
          type: "success",
          title: "✓ Signal Restored",
          message: "Sensor reconnected successfully.",
          duration: 3000,
        });
      }
    }
  }, [inGracePeriod, bothConnected, toast, sessionActive]);
  /* ── Stepper progress ── */
  let progress = 0;
  if (connected || selectedDeviceIds.length > 0) progress = 33;
  if (connected && signalValid) progress = 66;
  if (connected && signalValid && selectedDeviceIds.length > 0) progress = 100;

  /* ── Music ── */
  const handlePlay = async () => {
    if (!selectedDeviceIds.length) return;
    stopAll();
    try {
      await playOnMultipleDevices(selectedDeviceIds);
      setIsPlaying(true);
      setIsPaused(false);
    } catch {
      toast({
        type: "error",
        title: "Playback Error",
        message: "Failed to play audio",
      });
    }
  };
  const handlePause = () => {
    pauseAll();
    setIsPlaying(false);
    setIsPaused(true);
  };
  const handleResume = () => {
    resumeAll();
    setIsPlaying(true);
    setIsPaused(false);
  };
  const handleToggle = () => {
    if (isPlayingRef.current) return handlePause();
    if (isPausedRef.current) return handleResume();
    handlePlay();
  };
  const handleStop = () => {
    stopAll();
    setIsPlaying(false);
    setIsPaused(false);
  };

  /* ── Eye state ── */
  const [dataEyeState, setDataEyeState] = useState<EyeStateType>("open");
  const eyeState: EyeStateType = adminEyeOverride !== null ? adminEyeOverride : dataEyeState;

  /* ── REM detection (unchanged — still drives eyeState, independent of audio) ── */
  const remDetectorActive = sessionActive && bothConnected && practiceCompleted;

  useRemDetector({
    eogHistory,
    active: remDetectorActive,
    currentEyeState: dataEyeState,
    remThreshold, // always DEFAULT_REM_THRESHOLD = 500
    debugLogging: remDebugEnabled,
    onStateChange: (newState) => {
      setDataEyeState(newState);
      if (adminEyeOverride === null) {
        if (newState === "dreaming") {
          toast({
            type: "info",
            title: "💤 REM Detected",
            message: "Entering dreaming state.",
            duration: 4000,
          });
        } else if (newState === "half") {
          toast({
            type: "info",
            title: "🌑 REM Ended",
            message: "Movement calmed — returning to drowsy state.",
            duration: 3000,
          });
        } else {
          toast({
            type: "info",
            title: "🌑 State change",
            message: `Eye state → ${newState}`,
            duration: 3000,
          });
        }
      }
    },
  });

  /* ── dreamingActive — drives "REM · DREAMING" UI badge ── */
  const dreamingActive = eyeState === "dreaming" && sessionActive && bothConnected;

  /* ── Reactive affirmation audio ──────────────────────────────────────────
   * Starts at practice complete. Volume reacts to eye movement signal
   * relative to audioSensitivity (admin slider). Interval spacing between
   * clips is driven by affirmationInterval (Step 4 → Affirmation Timer).
   * ───────────────────────────────────────────────────────────────────────── */
  const reactiveAudioActive = sessionActive && bothConnected && practiceCompleted;
  // place this right after the `dreamingActive` / reactiveAudioActive block,
  // since rawBothConnected is already computed above

  const hypnagogicSignalPausedRef = useRef(false);

  useEffect(() => {
    if (!sessionActive || !practiceCompleted) return;

    if (!bothConnected) {
      // ← was rawBothConnected
      if (isHypnagogicOn) {
        hypnagogicSignalPausedRef.current = true;
        stopHypnagogic(0.6);
      }
    } else {
      if (hypnagogicSignalPausedRef.current) {
        hypnagogicSignalPausedRef.current = false;
        startHypnagogic(65);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bothConnected, sessionActive, practiceCompleted]);
  const markIntentionalDisconnect = useCallback(() => {
    intentionalDisconnectRef.current = true;
    // safety net in case the normal handleEndSession chain doesn't reset it
    setTimeout(() => {
      intentionalDisconnectRef.current = false;
    }, 3000);
  }, []);
  const {
    restartCycleNow,
    stopEverything: stopDreamAudio,
    unlockAudio,
  } = useReactiveDreamAudio({
    active: reactiveAudioActive,
    eogHistory,
    manualPaused: dreamAudioPaused,
    audioSensitivity,
    audioMinGain,
    audioMaxGain,
    affirmationIntervalSecs: affirmationInterval,
    onLabelChange: setAffirmationAudioLabel,
  });
  useEffect(() => {
    if (!reactiveAudioActive) setDreamAudioPaused(false);
  }, [reactiveAudioActive]);

  /* ── Admin play / stop handlers ── */
  const handleAdminPlayAffirmation = useCallback(() => {
    unlockAudio();
    if (dreamAudioPaused) {
      setDreamAudioPaused(false);
      toast({
        type: "info",
        title: "▶ Audio Resumed",
        message: "Continuing from where it paused.",
        duration: 2500,
      });
    } else {
      restartCycleNow();
      toast({
        type: "info",
        title: "💤 Playing Now",
        message: "Forced restart of affirmation loop.",
        duration: 2500,
      });
    }
  }, [dreamAudioPaused, restartCycleNow, toast, unlockAudio]);

  const handleAdminStopAffirmation = useCallback(() => {
    setDreamAudioPaused(true);
    toast({
      type: "info",
      title: "⏸ Audio Paused",
      message: "Click Play to resume.",
      duration: 2500,
    });
  }, [toast]);

  /* ── Session ── */
  const handleBeginSession = () => {
    unlockAudio();
    stopHypnagogic(0.5);
    stopDreamAudio();
    setDreamAudioPaused(false);
    setAffirmationAudioLabel(null);

    setSessionActive(true);
    setLoaderMessage("Entering Practice…");
    setPracticeSkipLoading(true);

    setTimeout(() => {
      setPracticeSkipLoading(false);

      if (DEV_SKIP_PRACTICE) {
        setShowPractice(true);
        setPracticeCompleted(true);
        setAdvancedView(false);
        startHypnagogic(65);
      } else {
        setShowPractice(true);
        setAdvancedView(false);
        setPracticeCompleted(false);
        setDataEyeState("open");
      }
    }, 1500);
  };
  const handleEndSession = () => {
    intentionalDisconnectRef.current = true;
    stopHypnagogic(1.5);
    setTimeout(() => {
      intentionalDisconnectRef.current = false;
    }, 2000);
    stopAll();
    stopDreamAudio();
    setIsPlaying(false);
    setIsPaused(false);
    setDreamAudioPaused(false);
    setAffirmationAudioLabel(null);
    try {
      disconnectEyeDevice();
    } catch {}
    setConnected(false);
    setCurrentAmplitude({ ch1: 0, ch2: 0, ch3: 0 });
    setHz(0);
    setBatteryLevel(null);
    setSelectedDeviceIds([]);
    setSessionActive(false);
    setShowPractice(false);
    setShowDashboard(false);
    setEogHistory([]);
    setHzHistory([]);
    setDataEyeState("open");
    setAdminEyeOverride(null);
    setAdvancedView(false);
    setPracticeCompleted(false);
    toast({
      type: "info",
      title: "Session Ended",
      message: "Sensor and audio disconnected.",
      duration: 3000,
    });
  };
  const handleLogout = useCallback(() => {
    // Clean up session exactly like End Session
    intentionalDisconnectRef.current = true;
    stopHypnagogic(1.5);
    setTimeout(() => {
      intentionalDisconnectRef.current = false;
    }, 2000);
    stopAll();
    stopDreamAudio();
    setIsPlaying(false);
    setIsPaused(false);
    setDreamAudioPaused(false);
    setAffirmationAudioLabel(null);
    try {
      disconnectEyeDevice();
    } catch {}
    setConnected(false);
    setCurrentAmplitude({ ch1: 0, ch2: 0, ch3: 0 });
    setHz(0);
    setBatteryLevel(null);
    setSelectedDeviceIds([]);
    setSessionActive(false);
    setShowPractice(false);
    setShowDashboard(false);
    setEogHistory([]);
    setHzHistory([]);
    setDataEyeState("open");
    setAdminEyeOverride(null);
    setAdvancedView(false);
    setPracticeCompleted(false);
  }, [stopHypnagogic, stopDreamAudio]);
  const handlePracticeComplete = useCallback(() => {
    setShowPractice(false);
    setPracticeCompleted(true);
    setDataEyeState("half");

    window.dispatchEvent(new Event("yogyism:stop"));

    setTimeout(() => {
      startHypnagogic(65);
      toast({
        type: "info",
        title: "🌙 Session Audio Started",
        message: "Affirmation audio is now playing softly and will respond to your eye movement.",
        duration: 5000,
      });
    }, 1500);
  }, [startHypnagogic, toast]);

  const handlePracticeSkip = useCallback(() => {
    setPracticeSkipLoading(true);
    setLoaderMessage("Entering Session…");
    setShowPractice(false);
    setPracticeCompleted(true);
    setDataEyeState("half");

    window.dispatchEvent(new Event("yogyism:stop"));

    setTimeout(() => {
      setPracticeSkipLoading(false);
      startHypnagogic(65);
      toast({
        type: "info",
        title: "🌙 Session Audio Started",
        message: "Affirmation audio is now playing softly and will respond to your eye movement.",
        duration: 4000,
      });
    }, 1500);
  }, [startHypnagogic, toast]);

  /* ── Sensor ── */
  const handleHzChange = (data: number | Amplitude) => {
    if (!sessionActiveRef.current) return;
    if (typeof data === "object") {
      const now = Date.now();
      setHz(data.ch1);
      if (now - amplitudeThrottleRef.current > 100) {
        amplitudeThrottleRef.current = now;
        setCurrentAmplitude(data);
        setEogHistory((prev) => {
          const next = [...prev, { time: now, ...data }];
          return next.length > MAX_HISTORY ? next.slice(-MAX_HISTORY) : next;
        });
      }
    } else {
      setHz(data);
      setHzHistory((prev) => {
        const next = [...prev, { time: Date.now(), hz: data }];
        return next.length > MAX_HISTORY ? next.slice(-MAX_HISTORY) : next;
      });
    }
  };

  /* ── Status text ── */
  const statusText = !connected
    ? "Connect your Dream Sensor to begin"
    : !refConnected
      ? "Reference pad not detected"
      : !signalValid
        ? "Waiting for signal..."
        : selectedDeviceIds.length === 0
          ? "Select a Bluetooth speaker"
          : "All set. Ready to start";

  /* ── Determine which overlay is visible on the right panel ──
     NOTE: `!showPractice` added to both so Practice always wins visually
     even if advancedView/showDashboard happen to still be true underneath
     it — this only matters for the mobile scroll-layer logic below and is
     a no-op on desktop (Practice already renders on top via zIndex there). */
  const showAdvanced = (bothConnected || sessionActive) && advancedView && !showPractice;
  const showDashboardOverlay = !showPractice && bothConnected && showDashboard && !advancedView;

  /* ── Left panel ── */
  const LeftPanelContent = (
    <SetupPane
      onHzChange={handleHzChange}
      onConnectionChange={setConnected}
      toast={toast}
      selectedDeviceIds={selectedDeviceIds}
      onDeviceSelectionChange={setSelectedDeviceIds}
      onBeginSession={handleBeginSession}
      onEndSession={handleEndSession}
      onManualDisconnectStart={markIntentionalDisconnect}
      sessionActive={sessionActive}
      showPractice={showPractice}
      isAdmin={isAdmin}
      practiceCompleted={practiceCompleted}
      autoAffirmation={autoAffirmation}
      onAutoAffirmationChange={setAutoAffirmation}
      // Affirmation Timer (Step 4) — now actually wired through to the audio
      // engine via affirmationIntervalSecs on useReactiveDreamAudio above.
      affirmationInterval={affirmationInterval}
      onAffirmationIntervalChange={setAffirmationInterval}
      // Pass audioSensitivity as remThreshold prop so the existing slider
      // in AdminPanel controls it — the label is updated in setup.tsx
      remThreshold={audioSensitivity}
      onRemThresholdChange={setAudioSensitivity}
      adminEyeOverride={adminEyeOverride}
      onAdminEyeOverrideChange={(state) => {
        setAdminEyeOverride(state);
        toast({
          type: "info",
          title: state !== null ? "🔧 Admin Override" : "🔧 Admin Override Cleared",
          message: state !== null ? `Eye state manually set to: ${state}` : "Eye state now follows sensor data.",
          duration: 2000,
        });
      }}
      dreamingActive={dreamingActive}
      dreamAudioPaused={dreamAudioPaused}
      onPlayAffirmationNow={handleAdminPlayAffirmation}
      onStopAffirmation={handleAdminStopAffirmation}
      dataEyeState={dataEyeState}
      remDebugEnabled={remDebugEnabled}
      onRemDebugToggle={setRemDebugEnabled}
      audioMinGain={audioMinGain}
      audioMaxGain={audioMaxGain}
      onAudioMinGainChange={setAudioMinGain}
      onAudioMaxGainChange={setAudioMaxGain}
      bothConnected={bothConnected}
      advancedView={advancedView}
      onToggleAdvanced={showPractice ? undefined : setAdvancedView}
    />
  );

  /* ── JSX ── */
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        paddingBottom: isMobile ? 56 : 0,
        overflow: "hidden",
      }}
    >
      {user && (
        <Header
          user={user}
          bothConnected={bothConnected}
          onLogout={handleLogout} // ← ADD THIS
        />
      )}

      {isMobile && <div className={`mobile-sheet-backdrop${panelOpen ? " visible" : ""}`} onClick={() => setPanelOpen(false)} />}

      <div className="split-layout" style={{ flex: 1, minHeight: 0 }}>
        {isMobile ? (
          <div className={`left-panel setup-panel${panelOpen ? " panel-open" : ""}`}>{LeftPanelContent}</div>
        ) : (
          <div className="left-panel setup-panel">{LeftPanelContent}</div>
        )}

        {/* ── RIGHT PANEL ──
            Desktop: unchanged — three absolute inset:0 layers crossfade
            on top of each other, `.right-panel` stays overflow:hidden.
            Mobile (≤900px, via home.css media query): the *visible* layer
            becomes a normal, scrollable block instead of an absolutely
            positioned overlay, and `.right-panel` becomes the scroll
            container — see the `rp-view-layer` rules in home.css. */}
        <div className="right-panel" style={{ position: "relative" }}>
          {sessionActive && inGracePeriod && !intentionalDisconnectRef.current && (
            <SignalLossBanner
              graceSecondsElapsed={graceSecondsElapsed}
              gracePctLeft={gracePctLeft}
              graceTotalSec={SIGNAL_LOSS_GRACE_MS / 1000}
            />
          )}
          {/* ── Skip loader overlay ── */}
          {practiceSkipLoading && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 20,
                background: "rgba(4, 9, 20, 0.88)",
                backdropFilter: "blur(6px)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 16,
                animation: "hypFadeIn 0.3s ease",
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  border: "3px solid #1e293b",
                  borderTop: "3px solid #38bdf8",
                  borderRadius: "50%",
                  animation: "spin 0.9s linear infinite",
                }}
              />
              <span
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: 11,
                  color: "#38bdf8",
                  letterSpacing: 1.5,
                  textTransform: "uppercase",
                  fontWeight: 700,
                }}
              >
                {loaderMessage}
              </span>
            </div>
          )}
          {((isHypnagogicOn && !showPractice && !sessionReportOpen) ||
            (reactiveAudioActive && !showPractice && affirmationAudioLabel && !sessionReportOpen)) && (
            <div
              style={{
                position: "absolute",
                top: 4,
                left: 12,
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <style>{`
      @keyframes hypFadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      @keyframes hypPulse  { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.4; transform: scale(0.85); } }
    `}</style>

              {isHypnagogicOn && !showPractice && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    background: "rgba(8, 6, 20, 0.82)",
                    border: "1px solid rgba(155, 140, 255, 0.30)",
                    borderRadius: 24,
                    padding: "4px 10px",
                    backdropFilter: "blur(10px)",
                    animation: "hypFadeIn 1s ease",
                  }}
                >
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: "#9b8cff",
                      display: "inline-block",
                      boxShadow: "0 0 8px #9b8cff88",
                      animation: "hypPulse 2.4s ease-in-out infinite",
                    }}
                  />
                  <span
                    style={{
                      fontFamily: FONT_MONO,
                      fontSize: 9,
                      letterSpacing: 0.8,
                      color: "#9b8cff",
                      textTransform: "uppercase",
                      fontWeight: 700,
                    }}
                  >
                    Hypnagogic · 3.5 Hz · 🎧
                  </span>
                </div>
              )}

              {reactiveAudioActive && !showPractice && affirmationAudioLabel && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    background: "rgba(8, 6, 20, 0.82)",
                    border: "1px solid rgba(200, 160, 80, 0.30)",
                    borderRadius: 24,
                    padding: "4px 10px",
                    backdropFilter: "blur(10px)",
                    animation: "hypFadeIn 0.5s ease",
                  }}
                >
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: "#c8a050",
                      display: "inline-block",
                      boxShadow: "0 0 8px #c8a05088",
                      animation: "hypPulse 2.4s ease-in-out infinite",
                    }}
                  />
                  <span
                    style={{
                      fontFamily: FONT_MONO,
                      fontSize: 9,
                      letterSpacing: 0.8,
                      color: "#c8a050",
                      textTransform: "uppercase",
                      fontWeight: 700,
                    }}
                  >
                    🗣️ {affirmationAudioLabel}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* ── Live affirmation label — shows which clip is currently
               playing ("You are dreaming…" / "Stay conscious…"), sitting
               just under the Hypnagogic badge. Hidden during the silent
               gap between clips (that gap length = Affirmation Timer). ──

               `rp-view-layer` + `data-hidden` below are used only by the
               mobile media query in home.css. On desktop they are inert —
               the inline styles are identical to before, so desktop
               rendering is unchanged. */}

          <div
            className="rp-view-layer rp-simpleview-layer"
            data-hidden={showAdvanced || showDashboardOverlay || showPractice ? "true" : "false"}
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 0,
              visibility: showAdvanced || showDashboardOverlay ? "hidden" : "visible",
              pointerEvents: showAdvanced || showDashboardOverlay ? "none" : "auto",
            }}
          >
            <SimpleView eyeState={eyeState} active={sessionActive} />
          </div>

          {showPractice && (
            <div className="rp-view-layer rp-practice-layer" data-hidden="false" style={{ position: "absolute", inset: 0, zIndex: 1 }}>
              <PracticeSwiperWrapper
                key="practice-swiper-stable"
                onSkip={handlePracticeSkip}
                onComplete={handlePracticeComplete}
                isAudioPlaying={isPlaying}
              />
            </div>
          )}

          <div
            className="rp-view-layer rp-dashboard-layer"
            data-hidden={showAdvanced || showDashboardOverlay ? "false" : "true"}
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 1,
              visibility: showAdvanced || showDashboardOverlay ? "visible" : "hidden",
              pointerEvents: showAdvanced || showDashboardOverlay ? "auto" : "none",
              opacity: showAdvanced || showDashboardOverlay ? 1 : 0,
            }}
          >
            <HzDashboard
              hzHistory={hzHistory}
              eogHistory={eogHistory}
              currentHz={hz}
              currentAmplitude={currentAmplitude}
              connected={connected}
              batteryLevel={batteryLevel}
              isPlaying={isPlaying}
              isPaused={isPaused}
              onPlayPause={handleToggle}
              onStop={handleStop}
              onOpenPractice={() => {
                setAdvancedView(false);
                setShowDashboard(false);
              }}
              eyeState={eyeState}
              remThreshold={remThreshold}
              onSessionReportChange={setSessionReportOpen}
            />
          </div>

          {sessionReportOpen && (
            <div
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 998,
                pointerEvents: "all",
              }}
            />
          )}
        </div>

        <ToastContainer toasts={toasts} dismiss={dismiss} />
      </div>

      {isMobile && (
        <nav className="mobile-tab-bar">
          <button className={`tab-btn${panelOpen ? " active" : ""}`} onClick={() => setPanelOpen((v) => !v)}>
            <span className="tab-icon">⚙️</span>
            <span className="tab-label">Setup</span>
          </button>
          <button className={`tab-btn${connected ? " active" : ""}`} onClick={() => setPanelOpen(false)}>
            <span className="tab-icon">{connected ? "✅" : "📡"}</span>
            <span className="tab-label">{connected ? "Connected" : "Connect"}</span>
          </button>
          <button
            className={`tab-btn${bothConnected && showDashboard ? " active" : ""}`}
            onClick={() => {
              setPanelOpen(false);
              if (bothConnected) setShowDashboard(true);
            }}
            disabled={!bothConnected}
            style={{ opacity: bothConnected ? 1 : 0.35 }}
          >
            <span className="tab-icon">📊</span>
            <span className="tab-label">Dashboard</span>
          </button>
          <button
            className={`tab-btn${bothConnected && !showDashboard ? " active" : ""}`}
            onClick={() => {
              setPanelOpen(false);
              if (bothConnected) setShowDashboard(false);
            }}
            disabled={!bothConnected}
            style={{ opacity: bothConnected ? 1 : 0.35 }}
          >
            <span className="tab-icon">🧘</span>
            <span className="tab-label">Practice</span>
          </button>
        </nav>
      )}
      {/* Copyright row — text centered, social icons pinned to the right corner */}
      <div
        style={{
          flexShrink: 0,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "4px 12px 0",
          minHeight: 22,
          background: "#040f18",
        }}
      >
        <div
          style={{
            fontSize: isMobile ? 9 : 10,
            color: "rgba(255,255,255,0.25)",
            letterSpacing: 0.3,
            textAlign: "center",
            padding: isMobile ? "0 60px" : 0,
          }}
        >
          © {new Date().getFullYear()} Dreamfulness- By Yogyism. All rights reserved.
        </div>

        <div
          style={{
            position: "absolute",
            right: 40,
            top: "50%",
            transform: "translateY(-50%)",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <button
            onClick={() => setShowInstagram(true)}
            aria-label="Instagram"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 17,
              height: 17,
              borderRadius: "50%",
              border: `1px solid rgba(${hexToRgb(HOME_ACCENT)},0.35)`,
              color: `rgba(${hexToRgb(HOME_ACCENT)},0.75)`,
              background: "transparent",
              cursor: "pointer",
              transition: "opacity 0.2s, border-color 0.2s, color 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = HOME_ACCENT;
              e.currentTarget.style.borderColor = `rgba(${hexToRgb(HOME_ACCENT)},0.85)`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = `rgba(${hexToRgb(HOME_ACCENT)},0.75)`;
              e.currentTarget.style.borderColor = `rgba(${hexToRgb(HOME_ACCENT)},0.35)`;
            }}
          >
            <FaInstagram size={40} />
          </button>

          <button
            onClick={() => setShowWhatsApp(true)}
            aria-label="WhatsApp"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 17,
              height: 17,
              borderRadius: "50%",
              border: `1px solid rgba(${hexToRgb(HOME_ACCENT)},0.35)`,
              color: `rgba(${hexToRgb(HOME_ACCENT)},0.75)`,
              background: "transparent",
              cursor: "pointer",
              transition: "opacity 0.2s, border-color 0.2s, color 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = HOME_ACCENT;
              e.currentTarget.style.borderColor = `rgba(${hexToRgb(HOME_ACCENT)},0.85)`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = `rgba(${hexToRgb(HOME_ACCENT)},0.75)`;
              e.currentTarget.style.borderColor = `rgba(${hexToRgb(HOME_ACCENT)},0.35)`;
            }}
          >
            <FaWhatsapp size={40} />
          </button>

          <button
            onClick={() => setShowYoutube(true)}
            aria-label="YouTube"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 17,
              height: 17,
              borderRadius: "50%",
              border: `1px solid rgba(${hexToRgb(HOME_ACCENT)},0.35)`,
              color: `rgba(${hexToRgb(HOME_ACCENT)},0.75)`,
              background: "transparent",
              cursor: "pointer",
              transition: "opacity 0.2s, border-color 0.2s, color 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = HOME_ACCENT;
              e.currentTarget.style.borderColor = `rgba(${hexToRgb(HOME_ACCENT)},0.85)`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = `rgba(${hexToRgb(HOME_ACCENT)},0.75)`;
              e.currentTarget.style.borderColor = `rgba(${hexToRgb(HOME_ACCENT)},0.35)`;
            }}
          >
            <FaYoutube size={40} />
          </button>

          <button
            onClick={() => setShowFacebook(true)}
            aria-label="Facebook"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 17,
              height: 17,
              borderRadius: "50%",
              border: `1px solid rgba(${hexToRgb(HOME_ACCENT)},0.35)`,
              color: `rgba(${hexToRgb(HOME_ACCENT)},0.75)`,
              background: "transparent",
              cursor: "pointer",
              transition: "opacity 0.2s, border-color 0.2s, color 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = HOME_ACCENT;
              e.currentTarget.style.borderColor = `rgba(${hexToRgb(HOME_ACCENT)},0.85)`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = `rgba(${hexToRgb(HOME_ACCENT)},0.75)`;
              e.currentTarget.style.borderColor = `rgba(${hexToRgb(HOME_ACCENT)},0.35)`;
            }}
          >
            <FaFacebook size={20} />
          </button>
        </div>
      </div>
      {/* ADD THE TWO MODALS HERE */}
      <SocialQrModal
        isOpen={showWhatsApp}
        onClose={() => setShowWhatsApp(false)}
        title="Join our WhatsApp Community"
        qrSrc="/image/whatsappQr.png"
        qrAlt="WhatsApp Community QR code"
        scanHint="Scan with your phone's camera or WhatsApp"
        link={SOCIAL_LINKS.whatsapp}
        buttonLabel="Open WhatsApp Web"
        accent={HOME_ACCENT}
        accentRgb={hexToRgb(HOME_ACCENT)}
      />

      <SocialQrModal
        isOpen={showInstagram}
        onClose={() => setShowInstagram(false)}
        title="Follow us on Instagram"
        qrSrc="/image/instagramQr.png"
        qrAlt="Instagram profile QR code"
        scanHint="Scan with your phone's camera or Instagram"
        link={SOCIAL_LINKS.instagram}
        buttonLabel="Open Instagram"
        accent={HOME_ACCENT}
        accentRgb={hexToRgb(HOME_ACCENT)}
      />

      <SocialQrModal
        isOpen={showYoutube}
        onClose={() => setShowYoutube(false)}
        title="Subscribe on YouTube"
        qrSrc="/image/YtQr.png"
        qrAlt="YouTube channel QR code"
        scanHint="Scan with your phone's camera"
        link={SOCIAL_LINKS.youtube}
        buttonLabel="Open YouTube"
        accent={HOME_ACCENT}
        accentRgb={hexToRgb(HOME_ACCENT)}
      />

      <SocialQrModal
        isOpen={showFacebook}
        onClose={() => setShowFacebook(false)}
        title="Follow us on Facebook"
        qrSrc="/image/FbQr.png"
        qrAlt="Facebook page QR code"
        scanHint="Scan with your phone's camera"
        link={SOCIAL_LINKS.facebook}
        buttonLabel="Open Facebook"
        accent={HOME_ACCENT}
        accentRgb={hexToRgb(HOME_ACCENT)}
      />
    </div>
  );
}
